import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Check } from "lucide-react";

const TIERS = [
  { key: "standard_assessment", name: "Standard Assessment", price: "$1,500",
    meta: "6-month lookback · up to 3 vendors / channels",
    blurb: "An independent review of a smaller or more concentrated marketing program." },
  { key: "expanded_assessment", name: "Expanded Assessment", price: "$2,500",
    meta: "12-month lookback · up to 5 vendors / channels",
    blurb: "A deeper view of where your marketing dollars went and what they produced.", popular: true },
];

export default function Register() {
  const [params] = useSearchParams();
  const initial = TIERS.some((t) => t.key === params.get("tier")) ? params.get("tier") : "standard_assessment";
  const [tier, setTier] = useState(initial);
  const [form, setForm] = useState({ firm_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const session_id = localStorage.getItem("rp_sid") || "";
      const { data } = await api.post("/auth/signup-checkout", {
        ...form, lookup_key: tier, session_id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-white/10 relative overflow-hidden">
        <div className="grain-overlay" />
        <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight relative z-10">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
        </Link>
        <div className="relative z-10">
          <h1 className="font-display text-5xl font-light leading-tight">Know what your marketing is actually producing.</h1>
          <p className="mt-6 text-zinc-400 max-w-md">Start a one-time Marketing Performance Assessment. Complete payment securely, then sign in with the password you set here to follow your assessment and findings.</p>
        </div>
        <p className="font-mono-data text-xs text-zinc-600 relative z-10">© 2026 RetainProof™</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <h2 className="font-display text-4xl font-light">Start your assessment</h2>
          <p className="text-zinc-500 text-sm mt-2 mb-6">Choose your assessment, enter your details, and continue to secure checkout.</p>

          {error && (
            <div className="mb-4 border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3" data-testid="register-error">{error}</div>
          )}

          <div className="space-y-3 mb-6">
            {TIERS.map((p) => (
              <button type="button" key={p.key} onClick={() => setTier(p.key)} data-testid={`register-plan-${p.key}`}
                className={`w-full text-left border p-4 transition-colors ${tier === p.key ? "border-[#D5A44A] bg-[#D5A44A]/10" : "border-white/15 hover:border-white/30"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg flex items-center gap-2">
                    {p.name}
                    {p.popular && <span className="bg-[#D5A44A] text-black text-[9px] font-mono-data uppercase tracking-widest px-1.5 py-0.5">Most popular</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-display text-xl">{p.price}</span>
                    {tier === p.key && <Check className="h-4 w-4 text-[#D5A44A]" />}
                  </span>
                </div>
                <p className="text-[11px] text-[#0E7C7B] mt-1 font-mono-data uppercase tracking-wide">{p.meta}</p>
                <p className="text-xs text-zinc-400 mt-1">{p.blurb}</p>
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {[
              { k: "firm_name", label: "Firm name", type: "text", tid: "register-firm-input" },
              { k: "name", label: "Your name", type: "text", tid: "register-name-input" },
              { k: "email", label: "Email", type: "email", tid: "register-email-input" },
              { k: "password", label: "Password (min 8 characters)", type: "password", tid: "register-password-input" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono-data">{f.label}</Label>
                <Input type={f.type} value={form[f.k]} onChange={upd(f.k)} required minLength={f.k === "password" ? 8 : undefined}
                  data-testid={f.tid} className="mt-2 bg-white/5 border-white/15 rounded-none text-white h-11" />
              </div>
            ))}
          </div>

          <Button type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 font-medium group">
            {loading ? "Redirecting to checkout…" : "Start Your Assessment"}
            {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
          </Button>

          <p className="mt-6 text-sm text-zinc-500">
            Already have an account?{" "}
            <Link to="/login" className="text-[#D5A44A] hover:underline" data-testid="to-login-link">Sign in</Link>
          </p>
          <p className="mt-2 text-xs text-zinc-600">Need a Comprehensive, Enterprise, or ongoing engagement? <Link to="/consultation" className="text-[#D5A44A] hover:underline">Talk to our team</Link>.</p>
        </form>
      </div>
    </div>
  );
}
